/**
 * GAME 5 — LUMIÈRES (akari, équité garantie par complétion d'indices).
 * Durci vs le prototype : chaque grille livrée est déductible par propagation
 * (gen chiffre des murs par vagues jusqu'à ce que akPropagate résolve, repli
 * sur le candidat le plus résolu — plus de fallback « à deviner »), plancher
 * de lampes par niveau, densités q/wd graduées, di2 quasi minimal + indices
 * de confort, vies [4,3,3], `par` = lampes de la solution.
 */
import type { GameEngine, PuzzleBase } from '@ordre-cache/shared';
import { ri } from '@ordre-cache/shared';
import { D4, deep, mk } from './util';

export interface LumieresPuzzle extends PuzzleBase {
  g: 'lumieres';
  walls: boolean[][];
  nums: number[][];
  sol: number[][];
  cur: number[][];
  K: number | null;
  lives: number | null;
  livesMax: number;
  flash?: [number, number] | null;
}

export function akLit(walls: boolean[][], lamps: number[][], N: number): boolean[][] {
  const lit = mk(N, false);
  for (let r = 0; r < N; r++)
    for (let c = 0; c < N; c++)
      if (lamps[r][c] === 1) {
        lit[r][c] = true;
        for (const [dr, dc] of D4) {
          let rr = r + dr,
            cc = c + dc;
          while (rr >= 0 && rr < N && cc >= 0 && cc < N && !walls[rr][cc]) {
            lit[rr][cc] = true;
            rr += dr;
            cc += dc;
          }
        }
      }
  return lit;
}
export function akSees(
  walls: boolean[][],
  lamps: number[][],
  N: number,
  r: number,
  c: number,
): boolean {
  for (const [dr, dc] of D4) {
    let rr = r + dr,
      cc = c + dc;
    while (rr >= 0 && rr < N && cc >= 0 && cc < N && !walls[rr][cc]) {
      if (lamps[rr][cc] === 1) return true;
      rr += dr;
      cc += dc;
    }
  }
  return false;
}
/** Essai-contradiction borné : au-delà de ce nombre d'inconnues, trop cher (et inutile pour certifier). */
const LOOK_MAX = 48;

/**
 * Cœur de la propagation, mute `st` (0 inconnu, 1 lampe, 2 sans-lampe).
 * Règles basiques + détection de contradictions ; à `depth > 0`, quand tout
 * stagne, essai-contradiction : une hypothèse qui casse fixe la case (technique
 * akari standard, ce qu'un joueur attentif déduit « et si je posais là ? »).
 * Retourne false sur contradiction.
 */
function akCore(p: LumieresPuzzle, st: number[][], depth: number): boolean {
  const N = p.N,
    walls = p.walls;
  let guard = 0;
  for (;;) {
    if (++guard > 400) break;
    let ch = false;
    for (let r = 0; r < N; r++)
      for (let c = 0; c < N; c++) {
        if (!walls[r][c] || p.nums[r][c] < 0) continue;
        const n = p.nums[r][c];
        const un: [number, number][] = [],
          la: [number, number][] = [];
        for (const [dr, dc] of D4) {
          const rr = r + dr,
            cc = c + dc;
          if (rr < 0 || rr >= N || cc < 0 || cc >= N || walls[rr][cc]) continue;
          if (st[rr][cc] === 1) la.push([rr, cc]);
          else if (st[rr][cc] === 0) un.push([rr, cc]);
        }
        if (la.length > n || la.length + un.length < n) return false;
        if (la.length === n) {
          for (const [rr, cc] of un) {
            st[rr][cc] = 2;
            ch = true;
          }
        } else if (la.length + un.length === n) {
          for (const [rr, cc] of un) {
            st[rr][cc] = 1;
            ch = true;
          }
        }
      }
    const lampG = st.map((r) => r.map((v) => (v === 1 ? 1 : 0)));
    const lit = akLit(walls, lampG, N);
    for (let r = 0; r < N; r++)
      for (let c = 0; c < N; c++) {
        if (st[r][c] === 1 && akSees(walls, lampG, N, r, c)) return false;
        if (st[r][c] === 0 && lit[r][c]) {
          st[r][c] = 2;
          ch = true;
        }
      }
    let placed = true;
    while (placed) {
      // « lit » est recalculé après CHAQUE lampe forcée (sinon fausses contradictions)
      placed = false;
      const lg2 = st.map((row) => row.map((v) => (v === 1 ? 1 : 0)));
      const lit2 = akLit(walls, lg2, N);
      for (let r = 0; r < N && !placed; r++)
        for (let c = 0; c < N && !placed; c++) {
          if (walls[r][c] || lit2[r][c]) continue;
          const cand: [number, number][] = [];
          if (st[r][c] === 0) cand.push([r, c]);
          for (const [dr, dc] of D4) {
            let rr = r + dr,
              cc = c + dc;
            while (rr >= 0 && rr < N && cc >= 0 && cc < N && !walls[rr][cc]) {
              if (st[rr][cc] === 0) cand.push([rr, cc]);
              rr += dr;
              cc += dc;
            }
          }
          if (cand.length === 1) {
            st[cand[0][0]][cand[0][1]] = 1;
            ch = true;
            placed = true;
          } else if (cand.length === 0) return false;
        }
    }
    if (ch) continue;
    if (depth > 0) {
      let unk = 0;
      for (let r = 0; r < N; r++)
        for (let c = 0; c < N; c++) if (!walls[r][c] && st[r][c] === 0) unk++;
      if (unk > 0 && unk <= LOOK_MAX) {
        for (let r = 0; r < N && !ch; r++)
          for (let c = 0; c < N && !ch; c++) {
            if (walls[r][c] || st[r][c] !== 0) continue;
            const t1 = st.map((row) => row.slice());
            t1[r][c] = 1;
            if (!akCore(p, t1, depth - 1)) {
              st[r][c] = 2;
              ch = true;
            } else {
              const t2 = st.map((row) => row.slice());
              t2[r][c] = 2;
              if (!akCore(p, t2, depth - 1)) {
                st[r][c] = 1;
                ch = true;
              }
            }
          }
      }
    }
    if (!ch) break;
  }
  return true;
}

/** Grille d'états après propagation complète, null si contradiction. */
function akSt(p: LumieresPuzzle): number[][] | null {
  const N = p.N;
  const st = mk(N, 0);
  for (let r = 0; r < N; r++) for (let c = 0; c < N; c++) if (p.walls[r][c]) st[r][c] = 2;
  return akCore(p, st, 1) ? st : null;
}

/** Certifie un état propagé : complet ET valide ⇒ grille de lampes, sinon false. */
function akComplete(p: LumieresPuzzle, st: number[][]): number[][] | false {
  const N = p.N;
  let full = true;
  const lampG = st.map((row) => row.map((v) => (v === 1 ? 1 : 0)));
  const lit = akLit(p.walls, lampG, N);
  for (let r = 0; r < N; r++)
    for (let c = 0; c < N; c++) {
      if (p.walls[r][c]) continue;
      if (st[r][c] === 0) full = false;
      if (!lit[r][c] && st[r][c] !== 1) full = false;
    }
  if (!full) return false;
  // validité complète avant de certifier « équitable » : chiffres exacts + aucune lampe n'en voit une autre
  for (let r = 0; r < N; r++)
    for (let c = 0; c < N; c++) {
      if (p.walls[r][c] && p.nums[r][c] >= 0) {
        let n = 0;
        for (const [dr, dc] of D4) {
          const rr = r + dr,
            cc = c + dc;
          if (rr >= 0 && rr < N && cc >= 0 && cc < N && lampG[rr][cc] === 1) n++;
        }
        if (n !== p.nums[r][c]) return false;
      }
      if (!p.walls[r][c] && lampG[r][c] === 1 && akSees(p.walls, lampG, N, r, c)) return false;
    }
  return lampG;
}

export function akPropagate(p: LumieresPuzzle): number[][] | false | null {
  const st = akSt(p);
  if (st === null) return null;
  return akComplete(p, st);
}

export const LUMIERES: GameEngine<LumieresPuzzle> = {
  id: 'lumieres',
  name: 'Lumières',
  icon: '💡',
  accent: '#ffe08a',
  tag: 'Éclaire chaque recoin de la grotte.',
  how: "Pose des <b>lampes</b> : chacune éclaire sa ligne et sa colonne jusqu'aux murs. But : <b>tout éclairer</b>, sans que deux lampes se voient. Un mur <b>chiffré</b> = nombre exact de lampes collées à lui. <b>Touche</b> : lampe → point-mémo → vide.",
  sizes: [5, 10, 14, 18],
  defis: [
    { n: 'La grotte', d: 'Pose tes lampes : chaque recoin doit recevoir la lumière.', ic: '🕯️' },
    {
      n: 'Murs bavards',
      d: 'Presque tous les murs affichent un chiffre, et ils sont pointilleux.',
      ic: '🔢',
    },
    { n: 'Murs muets', d: 'Le strict minimum de chiffres : la pierre garde ses secrets.', ic: '🤫' },
    { n: 'Lampes comptées', d: 'Le stock exact de lampes est affiché. Pas une de trop.', ic: '🧮' },
    {
      n: 'Trois vies',
      d: 'Chaque faux pas coûte une vie — et la grotte en accorde très peu.',
      ic: '❤️',
    },
    { n: 'Cathédrale', d: 'Une nef immense à illuminer, sans le moindre éblouissement.', ic: '⛪' },
  ],
  gen(di, N, diff, rng) {
    const wd = di === 5 ? [0.26, 0.3, 0.34][diff] : [0.16, 0.2, 0.24][diff];
    const q =
      di === 1
        ? [1, 0.85, 0.7][diff]
        : di === 2
          ? 0
          : di === 5
            ? [0.5, 0.4, 0.3][diff]
            : [0.65, 0.5, 0.4][diff];
    const floor = (N === 5 ? [5, 6, 7] : N === 10 ? [14, 17, 20] : [24, 28, 32])[diff];
    const lives = di === 4 ? [4, 3, 3][diff] : null;
    let best: LumieresPuzzle | null = null;
    let bestScore = -1;
    for (let t = 0; t < 60; t++) {
      const walls = mk(N, false).map((row) => row.map(() => rng() < wd));
      const lamps = mk(N, 0);
      let unlit: [number, number][] = [];
      let lit = mk(N, false);
      const recompute = (): void => {
        lit = akLit(walls, lamps, N);
        unlit = [];
        for (let r = 0; r < N; r++)
          for (let c = 0; c < N; c++) if (!walls[r][c] && !lit[r][c]) unlit.push([r, c]);
      };
      // couverture d'une lampe posée en (r,c) : cases encore sombres qu'elle éclairerait
      const gain = (r: number, c: number): number => {
        let g = 1;
        for (const [dr, dc] of D4) {
          let rr = r + dr,
            cc = c + dc;
          while (rr >= 0 && rr < N && cc >= 0 && cc < N && !walls[rr][cc]) {
            if (!lit[rr][cc]) g++;
            rr += dr;
            cc += dc;
          }
        }
        return g;
      };
      // une lampe collée à un mur est épinglable par le chiffre de ce mur :
      // les préférer réduit drastiquement les solutions multiples (équité)
      const wallAdj = (r: number, c: number): boolean => {
        for (const [dr, dc] of D4) {
          const rr = r + dr,
            cc = c + dc;
          if (rr >= 0 && rr < N && cc >= 0 && cc < N && walls[rr][cc]) return true;
        }
        return false;
      };
      recompute();
      let guard = 0;
      // niveau ↑ ⇒ on privilégie des lampes à faible couverture ⇒ solution plus dense
      const picks = [2, 3, 4][diff];
      while (unlit.length && ++guard < N * N * 2) {
        let [r, c] = unlit[ri(rng, unlit.length)];
        let key = (wallAdj(r, c) ? 0 : 1000) + (diff > 0 ? gain(r, c) : 0);
        for (let s = 1; s < picks; s++) {
          const [r2, c2] = unlit[ri(rng, unlit.length)];
          const k2 = (wallAdj(r2, c2) ? 0 : 1000) + (diff > 0 ? gain(r2, c2) : 0);
          if (k2 < key) {
            r = r2;
            c = c2;
            key = k2;
          }
        }
        lamps[r][c] = 1;
        recompute();
      }
      if (unlit.length) continue;
      const lampN = lamps.flat().reduce((a, b) => a + b, 0);
      // plancher de lampes par niveau — relâché après 40 tentatives (gen réussit toujours)
      if (t < 40 && lampN < floor) continue;
      const nums = mk(N, -1);
      const chiffre = (r: number, c: number): void => {
        let n = 0;
        for (const [dr, dc] of D4) {
          const rr = r + dr,
            cc = c + dc;
          if (rr >= 0 && rr < N && cc >= 0 && cc < N && lamps[rr][cc] === 1) n++;
        }
        nums[r][c] = n;
      };
      for (let r = 0; r < N; r++)
        for (let c = 0; c < N; c++) if (walls[r][c] && rng() < q) chiffre(r, c);
      const puz: LumieresPuzzle = {
        g: 'lumieres',
        pid: ri(rng, 1e9),
        N,
        di,
        par: lampN,
        walls,
        nums,
        sol: lamps,
        cur: mk(N, 0),
        K: di === 3 ? lampN : null,
        lives,
        livesMax: lives ?? 3,
      };
      // Certification par vague : règles basiques seules (rapide) ; lookahead
      // seulement proche du but (unk ≤ 16) ou à l'épuisement des murs — alors
      // strictement équivalent à akPropagate (depth 1 relancé depuis le point
      // fixe basique = depth 1 depuis zéro).
      const resolve = (final: boolean): { done: boolean; score: number } => {
        const st = mk(N, 0);
        for (let r = 0; r < N; r++) for (let c = 0; c < N; c++) if (walls[r][c]) st[r][c] = 2;
        if (akCore(puz, st, 0) && akComplete(puz, st)) return { done: true, score: N * N };
        let unk = 0;
        for (let r = 0; r < N; r++)
          for (let c = 0; c < N; c++) if (!walls[r][c] && st[r][c] === 0) unk++;
        if (!final && unk > 16) return { done: false, score: 0 };
        const done = akCore(puz, st, 1) ? !!akComplete(puz, st) : false;
        let score = 0;
        for (let r = 0; r < N; r++)
          for (let c = 0; c < N; c++) if (!walls[r][c] && st[r][c] !== 0) score++;
        return { done, score };
      };
      // ÉQUITÉ : tant que la grille n'est pas déductible, chiffrer 1 à 3 murs
      // de plus par vague (adjacents à une lampe de sol en priorité) — la
      // boucle termine car chaque vague chiffre au moins un mur.
      let res = resolve(false);
      while (!res.done) {
        const adj: [number, number][] = [];
        const rest: [number, number][] = [];
        for (let r = 0; r < N; r++)
          for (let c = 0; c < N; c++) {
            if (!walls[r][c] || nums[r][c] >= 0) continue;
            let a = false;
            for (const [dr, dc] of D4) {
              const rr = r + dr,
                cc = c + dc;
              if (rr >= 0 && rr < N && cc >= 0 && cc < N && lamps[rr][cc] === 1) a = true;
            }
            (a ? adj : rest).push([r, c]);
          }
        if (!adj.length && !rest.length) {
          res = resolve(true); // tous les murs chiffrés : verdict final avec lookahead
          break;
        }
        for (let k = 1 + ri(rng, 3); k > 0 && (adj.length || rest.length); k--) {
          const pool = adj.length ? adj : rest;
          const [r, c] = pool.splice(ri(rng, pool.length), 1)[0];
          chiffre(r, c);
        }
        res = resolve(false);
      }
      if (res.done) {
        if (di === 2) {
          // Murs muets : ensemble quasi minimal + indices de confort par niveau
          const un: [number, number][] = [];
          for (let r = 0; r < N; r++)
            for (let c = 0; c < N; c++) if (walls[r][c] && nums[r][c] < 0) un.push([r, c]);
          for (let k = [3, 1, 0][diff]; k > 0 && un.length; k--) {
            const [r, c] = un.splice(ri(rng, un.length), 1)[0];
            chiffre(r, c);
          }
        }
        return puz;
      }
      if (!best || res.score > bestScore) {
        bestScore = res.score;
        best = deep(puz);
      }
    }
    // repli : tous les murs chiffrés, candidat dont la propagation résout le
    // plus de cases — `best` existe toujours (le plancher est relâché dès t=40).
    return best as LumieresPuzzle;
  },
  tap(p, r, c) {
    if (p.walls[r][c] || p.lives === 0) return false;
    const v = p.cur[r][c];
    if (v === 0) {
      if (p.lives != null) {
        const test = p.cur.map((row) => row.slice());
        test[r][c] = 1;
        const lampG = test.map((row) => row.map((x) => (x === 1 ? 1 : 0)));
        let bad = akSees(p.walls, lampG, p.N, r, c);
        if (!bad)
          for (const [dr, dc] of D4) {
            const rr = r + dr,
              cc = c + dc;
            if (
              rr < 0 ||
              rr >= p.N ||
              cc < 0 ||
              cc >= p.N ||
              !p.walls[rr][cc] ||
              p.nums[rr][cc] < 0
            )
              continue;
            let n = 0;
            for (const [dr2, dc2] of D4) {
              const r2 = rr + dr2,
                c2 = cc + dc2;
              if (r2 >= 0 && r2 < p.N && c2 >= 0 && c2 < p.N && lampG[r2][c2] === 1) n++;
            }
            if (n > p.nums[rr][cc]) bad = true;
          }
        if (bad) {
          p.lives--;
          p.flash = [r, c];
          return true;
        }
      }
      p.cur[r][c] = 1;
    } else if (v === 1) p.cur[r][c] = 2;
    else p.cur[r][c] = 0;
    return true;
  },
  status(p) {
    const N = p.N;
    if (p.lives === 0) return { ok: false, txt: '💔 <b>Plus de vies…</b> 🔀 Nouvelle grotte' };
    const lampG = p.cur.map((r) => r.map((v) => (v === 1 ? 1 : 0)));
    const lit = akLit(p.walls, lampG, N);
    let dark = 0,
      see = false,
      numBad = false,
      lampN = 0;
    for (let r = 0; r < N; r++)
      for (let c = 0; c < N; c++) {
        if (p.walls[r][c]) {
          if (p.nums[r][c] >= 0) {
            let n = 0;
            for (const [dr, dc] of D4) {
              const rr = r + dr,
                cc = c + dc;
              if (rr >= 0 && rr < N && cc >= 0 && cc < N && lampG[rr][cc] === 1) n++;
            }
            if (n !== p.nums[r][c]) numBad = true;
          }
          continue;
        }
        if (!lit[r][c]) dark++;
        if (lampG[r][c] === 1) {
          lampN++;
          if (akSees(p.walls, lampG, N, r, c)) see = true;
        }
      }
    const kOk = p.K == null || lampN === p.K;
    if (!dark && !see && !numBad && kOk)
      return { ok: true, txt: '🎉 <b>Toute la grotte rayonne !</b>' };
    const bits = [`🕳 ${dark} cases sombres`];
    if (see) bits.push('⚠️ deux lampes se voient');
    if (numBad) bits.push('⚠️ compteur faux');
    if (p.K != null) bits.push(`💡 ${lampN}/${p.K}`);
    if (p.lives != null) bits.push('❤️'.repeat(p.lives) + '🖤'.repeat(p.livesMax - p.lives));
    return { ok: false, txt: bits.join(' · ') };
  },
  draw(p, ctx, layout) {
    const S = layout.size,
      N = p.N,
      cs = S / N;
    const lampG = p.cur.map((r) => r.map((v) => (v === 1 ? 1 : 0)));
    const lit = akLit(p.walls, lampG, N);
    const st = this.status(p);
    for (let r = 0; r < N; r++)
      for (let c = 0; c < N; c++) {
        const x = c * cs,
          y = r * cs;
        if (p.walls[r][c]) {
          ctx.fillStyle = '#494066';
          ctx.fillRect(x, y, cs, cs);
          ctx.fillStyle = '#332c4e';
          ctx.fillRect(x + cs * 0.08, y + cs * 0.08, cs * 0.84, cs * 0.84);
          if (p.nums[r][c] >= 0) {
            let n = 0;
            for (const [dr, dc] of D4) {
              const rr = r + dr,
                cc = c + dc;
              if (rr >= 0 && rr < N && cc >= 0 && cc < N && lampG[rr][cc] === 1) n++;
            }
            ctx.fillStyle =
              n === p.nums[r][c] ? '#7ee0b0' : n > p.nums[r][c] ? '#ff5470' : '#e8dcc0';
            ctx.font = `bold ${cs * 0.5}px sans-serif`;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(String(p.nums[r][c]), x + cs / 2, y + cs * 0.54);
          }
        } else {
          ctx.fillStyle = lit[r][c]
            ? st.ok
              ? 'rgba(255,224,138,.45)'
              : 'rgba(255,224,138,.32)'
            : '#0d0a1c';
          ctx.fillRect(x, y, cs, cs);
          if (p.cur[r][c] === 1) {
            const sees = akSees(p.walls, lampG, N, r, c);
            ctx.fillStyle = sees ? '#ff5470' : '#ffe08a';
            ctx.shadowColor = ctx.fillStyle;
            ctx.shadowBlur = cs * 0.5;
            ctx.beginPath();
            ctx.arc(x + cs / 2, y + cs / 2, cs * 0.24, 0, Math.PI * 2);
            ctx.fill();
            ctx.shadowBlur = 0;
          } else if (p.cur[r][c] === 2) {
            ctx.fillStyle = 'rgba(255,255,255,.3)';
            ctx.beginPath();
            ctx.arc(x + cs / 2, y + cs / 2, cs * 0.07, 0, Math.PI * 2);
            ctx.fill();
          }
        }
      }
    ctx.strokeStyle = 'rgba(255,255,255,.09)';
    ctx.lineWidth = 1;
    for (let i = 1; i < N; i++) {
      ctx.beginPath();
      ctx.moveTo(i * cs, 0);
      ctx.lineTo(i * cs, S);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(0, i * cs);
      ctx.lineTo(S, i * cs);
      ctx.stroke();
    }
    if (p.flash) {
      const [r, c] = p.flash;
      ctx.fillStyle = 'rgba(255,84,112,.5)';
      ctx.fillRect(c * cs, r * cs, cs, cs);
      p.flash = null;
    }
  },
  solveIt(p) {
    p.cur = p.sol.map((r) => r.slice());
  },
};
